import React, {useState, useEffect, useContext} from 'react';
import {Modal, Label, TextInput, Tabs} from 'flowbite-react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {getSettings, pushSettings} from '../../services/settingsService';
import {SettingsContext} from '../../App';
import {toast} from 'react-toastify';
import {
    settingsSchema,
    GRUPOS_ORDEN,
    validateValue,
    isValidEmail,
    isValidFecha,
    csvToList,
    listToCsv,
} from './settingsSchema';

// Tarea 6 — reescritura completa. Antes: lista plana de <TextInput> con
// nombres tecnicos, guardado por INDICE de fila (formElements[index]) — se
// rompia en cuanto los campos se reordenaban. Ahora: 3 pestañas (Jornada,
// Avisos, Precios), estado controlado por CLAVE (no por indice), etiquetas en
// español, input del tipo correcto por campo, y chips validados para emails y
// fechas no laborables.
const SettingsModal = ({isOpen, setIsOpen}) => {
    const [, setSettings] = useContext(SettingsContext);
    const [values, setValues] = useState({});
    const [originalValues, setOriginalValues] = useState({});
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const fetchAndInit = async () => {
            const fetched = await getSettings();
            setSettings(fetched);

            const knownKeys = new Set(settingsSchema.map((def) => def.key));
            const map = {};

            fetched.forEach((s) => {
                if (knownKeys.has(s.Key)) {
                    map[s.Key] = s.Value ?? '';
                } else {
                    // eslint-disable-next-line no-console
                    console.warn(`SettingsModal: clave desconocida en BD, se ignora en la interfaz: ${s.Key}`);
                }
            });

            // Claves del schema que no vinieran de BD (tabla vacia, o clave nueva
            // que aun no se ha sembrado) arrancan vacias en vez de undefined, para
            // que los inputs controlados no se quejen.
            settingsSchema.forEach((def) => {
                if (!(def.key in map)) map[def.key] = '';
            });

            setValues(map);
            setOriginalValues(map);
            setLoaded(true);
        };

        fetchAndInit();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- carga intencionada una sola vez al montar el modal
    }, []);

    const handleFieldChange = (key, value) => {
        setValues((prev) => ({...prev, [key]: value}));
    };

    // Autocomplete freeSolo dispara onChange con el array completo cada vez
    // (añadir o quitar un chip). Solo se valida el chip que se acaba de
    // añadir; si no es valido, se descarta y se avisa por toast en vez de
    // dejarlo entrar a la lista.
    const handleChipsChange = (key, type, newValueArray) => {
        const current = csvToList(values[key] || '');

        if (newValueArray.length > current.length) {
            const added = String(newValueArray[newValueArray.length - 1]).trim();
            const esValido = type === 'emails' ? isValidEmail(added) : isValidFecha(added);

            if (!esValido) {
                toast.error(
                    type === 'emails'
                        ? `"${added}" no tiene formato de email valido.`
                        : `"${added}" no tiene formato de fecha valido (usa aaaa-mm-dd).`
                );
                return;
            }

            handleFieldChange(key, listToCsv([...current, added]));
            return;
        }

        // Quitar un chip (o pegar una lista completa nueva mas corta).
        handleFieldChange(key, listToCsv(newValueArray.map((v) => String(v).trim())));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const errores = [];
        for (const def of settingsSchema) {
            if (def.type === 'emails' || def.type === 'dates') continue; // ya validados chip a chip
            const error = validateValue(def, values[def.key]);
            if (error) errores.push(error);
        }

        if (errores.length > 0) {
            errores.forEach((e) => toast.error(e));
            return;
        }

        const cambiados = settingsSchema
            .filter((def) => values[def.key] !== originalValues[def.key])
            .map((def) => ({Key: def.key, Value: values[def.key]}));

        if (cambiados.length > 0) {
            try {
                await pushSettings(cambiados);
                setSettings((prevSettings) => {
                    const porClave = new Map(prevSettings.map((s) => [s.Key, s]));
                    cambiados.forEach((c) => {
                        if (porClave.has(c.Key)) {
                            porClave.set(c.Key, {...porClave.get(c.Key), Value: c.Value});
                        } else {
                            porClave.set(c.Key, {Key: c.Key, Value: c.Value});
                        }
                    });
                    return Array.from(porClave.values());
                });
                setOriginalValues(values);
                toast.success('Ajustes guardados correctamente');
            } catch (error) {
                toast.error('No se pudieron guardar los ajustes: ' + error.message);
                return;
            }
        }

        setIsOpen(false);
    };

    const renderCampo = (def) => {
        const value = values[def.key] ?? '';

        if (def.type === 'emails' || def.type === 'dates') {
            return (
                <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={csvToList(value)}
                    onChange={(_event, newValue) => handleChipsChange(def.key, def.type, newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            placeholder={def.type === 'emails' ? 'nombre@ekipman.es' : 'aaaa-mm-dd'}
                        />
                    )}
                />
            );
        }

        if (def.type === 'time') {
            return (
                <TextInput
                    type="time"
                    value={value}
                    onChange={(e) => handleFieldChange(def.key, e.target.value)}
                />
            );
        }

        if (def.type === 'decimal') {
            return (
                <TextInput
                    type="number"
                    step="0.01"
                    min={def.min}
                    addon={def.unit}
                    value={value}
                    onChange={(e) => handleFieldChange(def.key, e.target.value)}
                />
            );
        }

        // integer
        return (
            <TextInput
                type="number"
                step="1"
                min={def.min}
                max={def.max}
                addon={def.unit}
                value={value}
                onChange={(e) => handleFieldChange(def.key, e.target.value)}
            />
        );
    };

    return (
        <Modal show={isOpen} onClose={() => setIsOpen(false)} size="2xl">
            <Modal.Header>Ajustes</Modal.Header>
            <Modal.Body>
                {!loaded ? (
                    <p>Cargando ajustes...</p>
                ) : (
                    <form onSubmit={handleSubmit} id="settingsForm">
                        {/* eslint-disable-next-line react/style-prop-object -- `style` aqui es la prop
                            de variante visual de flowbite-react (Tabs/theme.js), no el `style` de React */}
                        <Tabs.Group style="underline">
                            {GRUPOS_ORDEN.map((grupo) => (
                                <Tabs.Item key={grupo} title={grupo}>
                                    <div className="space-y-4 pt-2">
                                        {settingsSchema
                                            .filter((def) => def.group === grupo)
                                            .map((def) => (
                                                <div key={def.key} className="flex flex-col">
                                                    <Label htmlFor={def.key} className="font-semibold mb-1">
                                                        {def.label}
                                                    </Label>
                                                    {renderCampo(def)}
                                                </div>
                                            ))}
                                    </div>
                                </Tabs.Item>
                            ))}
                        </Tabs.Group>
                    </form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className="flex w-full items-center justify-center">
                    <button
                        type="submit"
                        form="settingsForm"
                        disabled={!loaded}
                        className="inline-flex text-lg justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-800 text-white hover:bg-blue-900 focus:outline-none sm:w-auto lg:text-xl disabled:opacity-50"
                    >
                        Guardar
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default SettingsModal;
